import React from "react";
import { Popover, Box, Typography, Button } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import {
  WbSunny as SunIcon,
  WbTwilight as SunsetIcon,
  NightsStay as MoonIcon,
} from "@mui/icons-material";

export interface TimeSlot {
  period: string;
  icon: SvgIconComponent;
  iconBg: string;
  iconColor: string;
  times: string[];
}

interface TimePickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  value: string;
  onChange: (time: string) => void;
  timeSlots?: TimeSlot[];
  title?: string;
  showCustomTime?: boolean;
  format?: "12h" | "24h";
}

const DEFAULT_TIME_SLOTS: TimeSlot[] = [
  {
    period: "Morning",
    icon: SunIcon,
    iconBg: "#FEF3C7",
    iconColor: "#D97706",
    times: ["06:00", "07:00", "08:00", "09:00", "10:00", "11:00"],
  },
  {
    period: "Afternoon",
    icon: SunsetIcon,
    iconBg: "#FFEDD5",
    iconColor: "#EA580C",
    times: ["12:00", "13:00", "14:00", "15:00", "16:00", "17:00"],
  },
  {
    period: "Evening",
    icon: MoonIcon,
    iconBg: "#F1F5F9",
    iconColor: "#475569",
    times: ["18:00", "19:00", "20:00", "21:00", "22:00", "23:00"],
  },
];

const formatTime = (time24: string, format: "12h" | "24h" = "12h") => {
  if (format === "24h") return time24;

  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

const TimePicker: React.FC<TimePickerProps> = ({
  open,
  anchorEl,
  onClose,
  value,
  onChange,
  timeSlots = DEFAULT_TIME_SLOTS,
  title = "Select a time",
  showCustomTime: _showCustomTime = true,
  format = "12h",
}) => {
  void _showCustomTime;
  const handleTimeClick = (time: string) => {
    onChange(time);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, md: 4 },
          p: { xs: 2, md: 2.5 },
          width: { xs: 340, md: 420 },
          maxHeight: "70vh",
          overflowY: "auto",
          mt: 1,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: { xs: "0.938rem", md: "1rem" },
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {timeSlots.map((slot) => {
          const Icon = slot.icon;

          return (
            <Box key={slot.period}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: slot.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: slot.iconColor }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {slot.period}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 0.75,
                  ml: { xs: 0, md: 5.5 },
                }}
              >
                {slot.times.map((time) => {
                  const isSelected = value === time;

                  return (
                    <Button
                      key={time}
                      onClick={() => handleTimeClick(time)}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        textTransform: "none",
                        transition: "all 0.2s",
                        bgcolor: isSelected ? "grey.900" : "grey.50",
                        color: isSelected ? "white" : "text.primary",
                        "&:hover": {
                          bgcolor: isSelected ? "grey.800" : "grey.100",
                          transform: isSelected ? "scale(1)" : "scale(1.05)",
                        },
                      }}
                    >
                      {formatTime(time, format)}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* {showCustomTime && (
        <Box sx={{ mt: 2.5, pt: 2.5, borderTop: 1, borderColor: "divider" }}>
          <Button
            fullWidth
            onClick={handleCustomTime}
            sx={{
              px: 2,
              py: 1,
              fontSize: "0.813rem",
              fontWeight: 500,
              color: "text.secondary",
              textTransform: "none",
              borderRadius: 2,
              "&:hover": {
                bgcolor: "grey.50",
              },
            }}
          >
            Custom time
          </Button>
        </Box>
      )} */}
    </Popover>
  );
};

export default TimePicker;
